import { useState, type FormEvent } from 'react';
import { Download, LoaderCircle, LogIn, Search } from 'lucide-react';
import type { AccountUser, Wallpaper } from '../types';
import { downloadWallpaper } from '../lib/downloadWallpaper';
import { supabase } from '../lib/supabase';
import { Dialog } from './Dialog';

type Order = {
  orderId: string;
  artworkId: string;
  status: string;
  environment: string;
  canDownload: boolean;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CODE = /^[a-f0-9]{64}$/i;

const ERRORS: Record<string, string> = {
  INVALID_REQUEST: '订单号或恢复码格式不正确。',
  ORDER_NOT_FOUND: '没有找到这个订单，请核对订单号和恢复码。',
  AUTH_REQUIRED: '恢复码无效或已过期。',
  EMAIL_NOT_VERIFIED: '请先完成邮箱验证。',
  EMAIL_MISMATCH: '当前账号邮箱与下单邮箱不一致，无法认领。',
  ORDER_NOT_PAID: '订单尚未支付，无法认领。',
  ALREADY_CLAIMED: '这笔订单已经绑定到其他账号。',
  PROOF_REQUIRED: '缺少订单恢复码，无法认领。',
  UPSTREAM: '服务暂时不可用，请稍后重试。',
};

async function token() {
  if (!supabase) return '';
  const session = await supabase.auth.getSession().catch(() => null);
  return session?.data.session?.access_token ?? '';
}

/**
 * Guest order lookup by order ID + one-time recovery code. Once the buyer is
 * signed in with the same email, the same code can claim the order into the
 * account so it appears in the library and downloads with a Bearer token.
 */
export function OrderRecoveryDialog({
  user,
  items,
  environment,
  onClose,
  onUnlocked,
  onLogin,
  notify,
}: {
  user: AccountUser | null;
  items: Wallpaper[];
  environment: 'sandbox' | 'live';
  onClose: () => void;
  onUnlocked: () => Promise<void>;
  onLogin: () => void;
  notify: (message: string) => void;
}) {
  const [orderId, setOrderId] = useState('');
  const [recovery, setRecovery] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [busy, setBusy] = useState<'lookup' | 'claim' | 'download' | ''>('');
  const [error, setError] = useState('');

  const artwork = order ? items.find((item) => item.id === order.artworkId) : undefined;

  async function lookup(event: FormEvent) {
    event.preventDefault();
    const id = orderId.trim().toLowerCase();
    const code = recovery.trim().toLowerCase();
    if (!UUID.test(id) || !CODE.test(code)) {
      setError(ERRORS.INVALID_REQUEST);
      return;
    }
    setBusy('lookup');
    setError('');
    setOrder(null);
    setClaimed(false);
    try {
      let response = await fetch(
        `/api/orders/${encodeURIComponent(id)}?environment=${environment}`,
        { headers: { 'X-Mujian-Recovery': code }, credentials: 'same-origin' },
      );
      let data = await response.json().catch(() => ({}));
      let viaAccount = false;
      if (!response.ok && response.status === 404 && user) {
        const accessToken = await token();
        if (accessToken) {
          response = await fetch(
            `/api/orders/${encodeURIComponent(id)}?environment=${environment}`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
              credentials: 'same-origin',
            },
          );
          data = await response.json().catch(() => ({}));
          viaAccount = true;
        }
      }
      if (!response.ok) throw new Error(data.error || 'UPSTREAM');
      setOrder(data as Order);
      setClaimed(viaAccount && data.canDownload === true);
    } catch (reason) {
      setError(ERRORS[(reason as Error).message] ?? ERRORS.UPSTREAM);
    } finally {
      setBusy('');
    }
  }

  async function claim() {
    if (!order) return;
    const accessToken = await token();
    if (!accessToken) {
      onLogin();
      return;
    }
    setBusy('claim');
    setError('');
    try {
      const response = await fetch(`/api/orders/${order.orderId}/claim`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Mujian-Recovery': recovery.trim().toLowerCase(),
        },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'UPSTREAM');
      setClaimed(true);
      await onUnlocked();
      notify('订单已认领到你的账号，可以在「我拥有的」中重新下载。');
    } catch (reason) {
      setError(ERRORS[(reason as Error).message] ?? ERRORS.UPSTREAM);
    } finally {
      setBusy('');
    }
  }

  async function download() {
    if (!order) return;
    if (!artwork) {
      setError('作品信息暂时不可用，请稍后再试。');
      return;
    }
    setBusy('download');
    setError('');
    try {
      await downloadWallpaper({ ...artwork, owned: true }, 'png');
      notify('文件已交给浏览器保存，请查看下载记录。');
    } catch (reason) {
      setError((reason as Error).message || ERRORS.UPSTREAM);
    } finally {
      setBusy('');
    }
  }

  return (
    <Dialog className="recovery-dialog" title="找回订单" onClose={onClose}>
      <div className="auth-content recovery-content">
        <p className="eyebrow">ORDER RECOVERY</p>
        <h2>找回订单</h2>
        <p className="recovery-copy">
          输入下单后显示的订单号和 64 位恢复码。登录下单邮箱的账号后，可把订单认领到账号并下载。
        </p>
        <form className="auth-form" onSubmit={lookup}>
          <div className="auth-field">
            <label htmlFor="recovery-order">订单号</label>
            <div className="auth-input-wrap">
              <Search size={18} />
              <input
                id="recovery-order"
                value={orderId}
                onChange={(event) => setOrderId(event.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                autoComplete="off"
                spellCheck={false}
                disabled={busy === 'lookup'}
              />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="recovery-code">恢复码</label>
            <div className="auth-input-wrap">
              <input
                id="recovery-code"
                value={recovery}
                onChange={(event) => setRecovery(event.target.value)}
                placeholder="64 位十六进制恢复码"
                autoComplete="off"
                spellCheck={false}
                disabled={busy === 'lookup'}
              />
            </div>
          </div>
          <button
            className="button button-dark auth-submit"
            type="submit"
            disabled={busy === 'lookup'}
          >
            {busy === 'lookup' ? <LoaderCircle size={18} className="spin" /> : <Search size={18} />}
            查询订单
          </button>
        </form>

        {order && (
          <div className="recovery-result" role="status">
            <div className="recovery-status">
              <span>状态</span>
              <strong>
                {order.status === 'paid'
                  ? '已支付'
                  : order.status === 'pending'
                    ? '待支付'
                    : order.status === 'refunded'
                      ? '已退款'
                      : '已关闭'}
              </strong>
            </div>
            {artwork && (
              <p className="recovery-artwork">
                {artwork.title} · {artwork.category}
              </p>
            )}
            {order.status === 'paid' ? (
              claimed ? (
                <button
                  className="button button-dark full-button"
                  disabled={busy === 'download'}
                  onClick={download}
                >
                  {busy === 'download' ? (
                    <LoaderCircle size={18} className="spin" />
                  ) : (
                    <Download size={18} />
                  )}
                  下载高清 PNG
                </button>
              ) : user ? (
                <button
                  className="button button-dark full-button"
                  disabled={busy === 'claim'}
                  onClick={claim}
                >
                  {busy === 'claim' ? (
                    <LoaderCircle size={18} className="spin" />
                  ) : (
                    <LogIn size={18} />
                  )}
                  认领到我的账号
                </button>
              ) : (
                <button className="button button-dark full-button" onClick={onLogin}>
                  <LogIn size={18} />
                  登录后认领
                </button>
              )
            ) : (
              <p className="sandbox-hint">
                {order.status === 'pending'
                  ? '订单还没有支付完成，请回到创建订单的浏览器继续支付。'
                  : '这笔订单已关闭，无法下载。'}
              </p>
            )}
          </div>
        )}
        {error && (
          <p className="auth-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
